import { useState } from 'react';
import { Plus, Pencil, Trash2, Sparkles, Link } from 'lucide-react';
import {
  useSendTemplates,
  useCreateSendTemplate,
  useUpdateSendTemplate,
  useDeleteSendTemplate,
  useSeedDefaultTemplates,
  useCalendarLink,
  useUpdateCalendarLink,
} from '@/hooks/useSendTemplates';
import { Button, Card, EmptyState, Input, Modal, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { VariableField } from '@/components/VariableField';
import { TEMPLATE_VARIABLES } from '@/lib/templateVariables';
import type { SendTemplate } from '@/lib/api';

export default function SendTemplateSettings() {
  const { data: templates = [], isLoading } = useSendTemplates();
  const { data: calendarLink } = useCalendarLink();
  const createTemplate = useCreateSendTemplate();
  const updateTemplate = useUpdateSendTemplate();
  const deleteTemplate = useDeleteSendTemplate();
  const seedDefaults = useSeedDefaultTemplates();
  const updateCalendarLink = useUpdateCalendarLink();

  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SendTemplate | null>(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '' });
  const [calendarInput, setCalendarInput] = useState('');
  const [showCalendarEdit, setShowCalendarEdit] = useState(false);

  const handleEdit = (template: SendTemplate) => {
    setEditingTemplate(template);
    setForm({ name: template.name, subject: template.subject, body: template.body });
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setForm({ name: '', subject: '', body: '' });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (editingTemplate) {
      await updateTemplate.mutateAsync({ id: editingTemplate.id, ...form });
    } else {
      await createTemplate.mutateAsync(form);
    }
    setShowEditor(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    await deleteTemplate.mutateAsync(id);
  };

  const handleSaveCalendarLink = async () => {
    await updateCalendarLink.mutateAsync(calendarInput);
    setShowCalendarEdit(false);
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-heading-2 text-secondary">Send Templates</h2>
          <p className="text-body-sm text-grey-500 mt-1">
            Create reusable email templates for outreach.
          </p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => seedDefaults.mutateAsync()}
              loading={seedDefaults.isPending}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Load Defaults
            </Button>
          )}
          <Button size="sm" onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-1" />
            New Template
          </Button>
        </div>
      </div>

      {/* Calendar Link */}
      <Card padding="sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link className="w-5 h-5 text-grey-400" />
            <div>
              <p className="text-sm font-medium text-secondary">Calendar Link</p>
              <p className="text-xs text-grey-500">
                {calendarLink || 'Not set - used for {{user.calendarLink}} variable'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Edit calendar link"
            onClick={() => {
              setCalendarInput(calendarLink || '');
              setShowCalendarEdit(true);
            }}
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {/* Templates List */}
      <div className="space-y-3">
        {templates.map((template) => (
          <Card key={template.id} padding="sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-secondary">{template.name}</h3>
                  {!template.isActive && <Badge variant="default">Inactive</Badge>}
                </div>
                <p className="text-sm text-grey-500 mt-1 truncate">{template.subject}</p>
                <p className="text-xs text-grey-400 mt-2 line-clamp-2">{template.body}</p>
              </div>
              <div className="flex items-center gap-1 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${template.name}`}
                  onClick={() => handleEdit(template)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${template.name}`}
                  onClick={() => handleDelete(template.id)}
                  disabled={deleteTemplate.isPending}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {templates.length === 0 && (
          <Card>
            <EmptyState title="No templates yet" description="Create one or load defaults." />
          </Card>
        )}
      </div>

      {/* Template Editor Dialog */}
      <Modal
        open={showEditor}
        onOpenChange={setShowEditor}
        size="lg"
        title={editingTemplate ? 'Edit Template' : 'New Template'}
        footer={
          <>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.name || !form.subject || !form.body}
              loading={createTemplate.isPending || updateTemplate.isPending}
            >
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Intro Call Request"
          />

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Subject</label>
            <VariableField
              value={form.subject}
              onChange={(v) => setForm((prev) => ({ ...prev, subject: v }))}
              placeholder="e.g., Let's schedule a call - {{deal.name}}"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Body</label>
            <VariableField
              multiline
              rows={10}
              value={form.body}
              onChange={(v) => setForm((prev) => ({ ...prev, body: v }))}
              placeholder="Write your email template... Type {{ to insert a variable."
            />
            <p className="text-xs text-grey-400 mt-1">
              Type <span className="font-mono">{'{{'}</span> to insert a variable, or click one below.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-2">Variables</label>
            <div className="space-y-2">
              {(['Contact', 'Deal', 'User'] as const).map((group) => (
                <div key={group}>
                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wide mb-1">
                    {group}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {TEMPLATE_VARIABLES.filter((v) => v.group === group).map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({ ...prev, body: prev.body + v.key }))
                        }
                        title={v.key}
                        className="px-2 py-1 text-xs bg-grey-100 hover:bg-grey-200 rounded text-grey-700"
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Calendar Link Dialog */}
      <Modal
        open={showCalendarEdit}
        onOpenChange={setShowCalendarEdit}
        title="Calendar Link"
        description={
          <>
            Enter your booking link (e.g., Calendly, HubSpot). This will be used when you insert
            the <code className="bg-grey-100 px-1 rounded">{'{{user.calendarLink}}'}</code> variable.
          </>
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setShowCalendarEdit(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCalendarLink} loading={updateCalendarLink.isPending}>
              Save
            </Button>
          </>
        }
      >
        <Input
          label="Booking link"
          value={calendarInput}
          onChange={(e) => setCalendarInput(e.target.value)}
          placeholder="https://calendly.com/your-link"
        />
      </Modal>
    </div>
  );
}
